import React, { useEffect } from "react";
import { Container, Grid, Typography, Paper, Box } from "@mui/material";
import DashboardLayout from "../../layouts/DashboardLayout";
import useStyles from "./styles";
import { useNavigate } from "react-router-dom";

import { getSuperadminDashboard } from "../../redux/actions/dashboard";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as TotalCompaniesIcon } from "../../assets/SuperAdminDashboard/companies.svg";
import { ReactComponent as TotalAuditsIcon } from "../../assets/SuperAdminDashboard/audits.svg";
import { ReactComponent as TotalUsersIcon } from "../../assets/SuperAdminDashboard/Totalusers.svg";

import { ReactComponent as usersTotal } from "../../assets/SuperAdminDashboard/usersTotal.svg";
import { ReactComponent as inactiveUsers } from "../../assets/SuperAdminDashboard/InactiveUsers.svg";
import { ReactComponent as LockedUsers } from "../../assets/SuperAdminDashboard/lockedUsers.svg";
import { ReactComponent as disabledUsers } from "../../assets/SuperAdminDashboard/disabledUsers.svg";
import { ReactComponent as readOnlyUsers } from "../../assets/SuperAdminDashboard/readOnlyUsers.svg";

import { ReactComponent as subscriptionActive } from "../../assets/SuperAdminDashboard/subscriptionActive.svg";
import { ReactComponent as subscription30 } from "../../assets/SuperAdminDashboard/subscription30.svg";
import { ReactComponent as subscriptionReadOnly } from "../../assets/SuperAdminDashboard/subscriptionReadOnly.svg";
import { ReactComponent as subscriptionDeactive } from "../../assets/SuperAdminDashboard/subscriptionDeactive.svg";
import { ReactComponent as subscriptionGrace } from "../../assets/SuperAdminDashboard/subscriptionGrace.svg";
import { superAdminCompanyOverview } from "../../redux/actions";

const DashboardCard = ({
  color,
  icon,
  value,
  label,
  cardClass,
  style,
  onClick,
}) => (
  <Paper
    elevation={3}
    sx={{ p: 2, borderRadius: 2 }}
    style={style}
    className={cardClass}
  >
    <Grid
      container
      alignItems="center"
      spacing={2}
      style={{
        display: "flex",
        flexDirection: "column",
      }}
      onClick={
        onClick
          ? onClick
          : () => {
              console.log("");
            }
      }
    >
      <Grid
        item
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "baseline",
        }}
      >
        {icon}
        <Typography
          variant="h5"
          fontWeight="bold"
          color={color}
          style={{ paddingLeft: "5px" }}
        >
          {value}
        </Typography>
      </Grid>
      <Grid item>
        <Typography color={color} variant="subtitle2">
          {label}
        </Typography>
      </Grid>
    </Grid>
  </Paper>
);

const SuperAdminDashboard = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getSuperadminDashboard(props.filter));
    dispatch(superAdminCompanyOverview());
  }, [dispatch, props.filter]);

  const statisticsData = useSelector(
    (state) => state.dashboard.getAdminDashboard.data
  );
  const companyOverviewData = useSelector(
    (state) => state.superAdmin.superAdminCompanyOverview.data
  );

  return (
    <DashboardLayout>
      <Container className={classes.container}>
        <Box p={2}>
          {/* Top Summary */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <DashboardCard
                color="error"
                icon={<TotalCompaniesIcon />}
                value={statisticsData?.company_count}
                label="Total Companies"
                cardClass={classes.mainCard}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DashboardCard
                color="#00BDB1"
                icon={<TotalAuditsIcon />}
                value={statisticsData?.audited_reports_count}
                label="Audited Reports"
                cardClass={classes.mainCard}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DashboardCard
                color="#9747FF"
                icon={<TotalUsersIcon />}
                value={statisticsData?.total_tickets_count}
                label="Total Tickets"
                cardClass={classes.mainCard}
              />
            </Grid>
          </Grid>

          {/* User Status Overview */}
          <Box mt={4} className={classes.userOverViewCard}>
            <Typography variant="h6" gutterBottom>
              User Status Overview
            </Typography>
            <Grid container spacing={2}>
              {[
                {
                  label: "Total Users",
                  value: companyOverviewData?.user_status_widget?.total_users,
                  icon: usersTotal,
                  color: "#024FE4",
                  type: "totalUsers",
                },
                {
                  label: "Inactive Users",
                  value:
                    companyOverviewData?.user_status_widget?.inactive_users,
                  icon: inactiveUsers,
                  color: "#7B7B7B",
                  type: "inactiveUsers",
                },
                {
                  label: "Locked Users",
                  value: companyOverviewData?.user_status_widget?.locked_users,
                  icon: LockedUsers,
                  color: "#E81111",
                  type: "lockedUsers",
                },
                {
                  label: "Disabled Users",
                  value:
                    companyOverviewData?.user_status_widget?.disabled_users,
                  icon: disabledUsers,
                  color: "#F7931D",
                  type: "disabledUsers",
                },
                {
                  label: "Read only mode users",
                  value:
                    companyOverviewData?.user_status_widget?.read_only_users,
                  icon: readOnlyUsers,
                  color: "#0088C7",
                  type: "readOnlyUsers",
                },
              ].map((item, i) => (
                <Grid item xs={12} sm={6} md={2.4} key={i}>
                  <DashboardCard
                    color={item.color}
                    icon={<item.icon />}
                    value={item.value}
                    label={item.label}
                    cardClass={classes.subCardUser}
                    onClick={() => navigate(`details/user/${item.type}`)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Subscription Overview */}
          <Box mt={4} className={classes.customerOverViewCard}>
            <Typography variant="h6" gutterBottom>
              Customer Subscription Overview
            </Typography>
            <Grid container spacing={2} className={classes.customerSub}>
              {[
                {
                  label: "Active Subscriptions",
                  value: companyOverviewData?.company_widget?.active,
                  icon: subscriptionActive,
                  color: "#48742C",
                  type: "active",
                },
                {
                  label: "Expiring in 30 Days",
                  value:
                    companyOverviewData?.company_widget?.expiring_in_30_days,
                  icon: subscription30,
                  color: "#F7931D",
                  type: "expireIn30Days",
                },
                {
                  label: "Read Only Accounts",
                  value: companyOverviewData?.company_widget?.read_only_mode,
                  icon: subscriptionReadOnly,
                  color: "#0088C7",
                  type: "readOnlyMode",
                },
              ].map((item, i) => (
                <Grid item xs={12} sm={6} md={2.4} key={i}>
                  <DashboardCard
                    color={item.color}
                    icon={<item.icon />}
                    value={item.value}
                    label={item.label}
                    style={{ borderColor: item.color }}
                    cardClass={classes.subCardSubscription}
                    onClick={() => navigate(`details/company/${item.type}`)}
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={2} className={classes.customerSubLower}>
              {[
                {
                  label: "In Grace Period",
                  value: companyOverviewData?.company_widget?.in_grace_period,
                  icon: subscriptionGrace,
                  color: "#8C2148",
                  type: "inGracePeriod",
                },
                {
                  label: "Deactivated Accounts",
                  value: companyOverviewData?.company_widget?.cancelled,
                  icon: subscriptionDeactive,
                  color: "#E81111",
                  type: "cacelled",
                },
              ].map((item, i) => (
                <Grid item xs={12} sm={6} md={2.4} key={i}>
                  <DashboardCard
                    color={item.color}
                    icon={<item.icon />}
                    value={item.value}
                    label={item.label}
                    style={{ borderColor: item.color }}
                    cardClass={classes.subCardSubscription}
                    onClick={() => navigate(`details/company/${item.type}`)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Container>
    </DashboardLayout>
  );
};

export default SuperAdminDashboard;
